/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addFieldError, removeFieldError } from 'lib/actions';
import useTranslate from 'lib/languages/useTranslate';
import { getFieldError, getFieldErrorsVisible } from 'lib/selectors';
import { WithFieldErrorsState } from 'lib/selectors/types';
import { isEmpty, usePrevious } from 'lib/utils';
import Typography from 'components/shared/generic/typography/presentational/Typography';

interface IOptions {
    value: string;
    text: string;
    label?: string;
    subLabel?: string;
}

interface IWrapperProps {
    name: string;
    type?: string;
    info?: string;
    value: string;
    required?: boolean;
    validate?: () => void;
    onChange: (name: string, value: string, ...rest) => void;
    validationRegexp?: RegExp;
    maxLength?: number;
    minLength?: number;
    options?: IOptions[];
}

interface IWrapper extends IWrapperProps {
    onChange: () => void;
    showError: () => void;
    hideError: () => void;
}

const withFieldValidation = (WrappedComponent: React.FC<IWrapper>) => props => {
    const dispatch = useDispatch();
    const [showError, setShowError] = useState(false);
    const error = useSelector((state: WithFieldErrorsState) => getFieldError(state, props));
    const errorsVisible = useSelector(getFieldErrorsVisible);
    const prevValue = usePrevious(props.value);
    const prevErrorsVisible = usePrevious(errorsVisible);
    const translate = useTranslate('withFieldValidation');

    useEffect(() => {
        _validate(props.value);
        return () => {
            dispatch(removeFieldError(props.name));
        };
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (isEmpty(prevValue) && !isEmpty(props.value)) _validate(props.value);
        if (!prevErrorsVisible && errorsVisible) handleShowError();
    });

    const customErrorStyle =
        showError && error ? { classes: `${props.classes} error-Input` } : {};

    return (
        <>
            <WrappedComponent
                {...props}
                showError={handleShowError}
                hideError={handleShowError}
                onChange={handleChange}
                {...customErrorStyle}
            />
            {props.info && !showError && (
                <Typography className="info">{props.info}</Typography>
            )}
            {showError && error && (
                <p className="error red-text text-accent-4">{error}</p>
            )}
        </>
    );

    function handleShowError() {
        setShowError(prev => !prev);
    }

    function handleChange(name, value, ...rest) {
        _validate(value);
        props.onChange(name, value, ...rest);
    }

    function _validate(value) {
        const {
            name,
            required,
            validate = () => {},
            validationRegexp,
            maxLength,
            minLength,
        } = props;

        const customError = validate(value);

        function handleFieldValueValidate(): string | null {
            switch (true) {
                case !!customError:
                    return customError;
                case required && isEmpty(value):
                    return translate('required');
                case value &&
                    validationRegexp &&
                    !RegExp(validationRegexp).test(value):
                    return translate('invalidValue');
                case maxLength && value.length > maxLength:
                    return `${translate('shorterThan')} ${maxLength}.`;
                case minLength && value.length < minLength:
                    return `${translate('longerThan')} ${minLength}.`;
                default:
                    return null;
            }
        }

        const validateValue = handleFieldValueValidate();
        if (validateValue) {
            dispatch(addFieldError({ name, value: validateValue }));
        } else if (error) {
            dispatch(removeFieldError(name));
        }
    }
};

export default withFieldValidation;
